import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * <hf-price>: keeps the visible HT / TTC pair in sync with its hidden `.price` source.
 * Apps such as Easify options rewrite `.price` elements (e.g. to add option prices);
 * this component recomputes both amounts whenever that source text changes.
 * Deliberately a plain HTMLElement: one observer, no refs or events.
 */
class HfPrice extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    const source = this.querySelector('.hf-price__source');
    if (!source) return;
    this.#observer = new MutationObserver(() => this.#sync(source));
    this.#observer.observe(source, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /** @param {Element} source */
  #sync(source) {
    const { rate, taxesIncluded, currency = 'EUR', format = '{{amount}}' } = this.dataset;
    const amount = convertMoneyToMinorUnits(source.textContent ?? '', currency);
    if (amount == null) return;

    const factor = (100 + Number(rate || 0)) / 100;
    const ht = taxesIncluded === 'true' ? Math.round(amount / factor) : amount;
    const ttc = taxesIncluded === 'true' ? amount : Math.round(amount * factor);

    const htTarget = this.querySelector('[data-hf-price="ht"]');
    const ttcTarget = this.querySelector('[data-hf-price="ttc"]');
    if (htTarget) htTarget.textContent = formatMoney(ht, format, currency);
    if (ttcTarget) ttcTarget.textContent = formatMoney(ttc, format, currency);
    // A zero price (configurator before any choice) shows a label instead of 0,00 €.
    this.toggleAttribute('data-zero', amount === 0);
  }
}

if (!customElements.get('hf-price')) {
  customElements.define('hf-price', HfPrice);
}
